import "./styles.css";
import { escapeHtml } from "./dashboard-html.js";
import { leadBriefRequested, renderLeadBrief } from "./dashboard-lead-brief.js";
import { renderPipelineBoard } from "./dashboard-pipeline-board.js";
import { bindLeadFactoryActions, ensureLeadDetailNavigationBound } from "./dashboard-lead-factory-bind.js";
import { mustFind } from "./dashboard-render-helpers.js";
import { showSourceDocumentReview } from "./dashboard-lead-detail-nav.js";
import { directToolbenchRetryRunId, renderDirectToolbenchRetry } from "./dashboard-direct-toolbench-retry.js";
import { directSpLookupRequested, renderDirectSpLookup } from "./dashboard-direct-sp-lookup.js";
import { raisePackageRequested, renderDirectRaisePackage } from "./dashboard-direct-raise-package.js";
import { ingestRequested, renderRetiredGoRules, toolsRequested } from "./dashboard-pipeline-nav.js";
import { renderPipelineIngest } from "./dashboard-pipeline-ingest.js";
import { renderTreesPipelineOverview, treesPipelineListQuery } from "./dashboard-trees-pipeline.js";
import { renderPipelineTools } from "./dashboard-pipeline-tools.js";
import {
  directSourceDocumentReviewId,
  renderDirectSourceDocumentReview
} from "./dashboard-direct-source-review.js";
import {
  directDownstreamLeadIdRequested,
  renderDirectDownstreamLead
} from "./dashboard-direct-downstream.js";
import { directFollowUpRequested, renderDirectFollowUp } from "./dashboard-direct-follow-up.js";
import { directSavedViewManagerId, renderDirectSavedViewManager } from "./dashboard-direct-saved-view.js";
import { directContactWorkRequested, renderDirectContactWork } from "./dashboard-direct-contact-work.js";
import {
  directContactAcquisitionRequested,
  renderDirectContactAcquisitionWorkbench
} from "./dashboard-direct-contact-acquisition.js";
import {
  app,
  dashboardTokenStorageKey,
  dashboardOperatorStorageKey,
  dashboardRoleStorageKey
} from "./dashboard-urls";
import type { SourceDocumentReviewFocus } from "./dashboard-lead-factory-types.js";
import {
  DashboardAuthRequiredError,
  hasDashboardCredentials
} from "./dashboard-auth";

if (!app) throw new Error("Dashboard mount element not found.");
const mount = app;

async function boot(): Promise<void> {
  const alreadyMounted = Boolean(mount.querySelector("[data-pipeline-board], header.topbar"));
  if (!alreadyMounted) {
    mount.innerHTML = `<main class="loading">Loading dashboard...</main>`;
  }

  try {
    if (raisePackageRequested()) {
      await renderDirectRaisePackage(mount, renderAuthPrompt);
      return;
    }
    if (leadBriefRequested()) {
      await renderLeadBrief(mount);
      return;
    }
    if (!hasDashboardCredentials()) {
      renderAuthPrompt("Enter the dashboard password.");
      return;
    }
    if (window.location.hash === "#gorules") {
      renderRetiredGoRules(mount);
      return;
    }
    if (ingestRequested()) {
      await renderPipelineIngest(mount);
      return;
    }
    // Pile list only. Bare /trees-pipeline is the swimlane board (stolen twice).
    if (treesPipelineListQuery()) {
      await renderTreesPipelineOverview(mount);
      return;
    }
    if (toolsRequested()) {
      await renderPipelineTools(mount);
      return;
    }
    const directRetryRunId = directToolbenchRetryRunId();
    if (directRetryRunId) {
      await renderDirectToolbenchRetry(mount, directRetryRunId);
      return;
    }
    if (directSpLookupRequested()) {
      await renderDirectSpLookup(mount);
      return;
    }
    const directSourceDocumentId = directSourceDocumentReviewId();
    if (directSourceDocumentId) {
      await renderDirectSourceDocumentReview(mount, directSourceDocumentId, showReview);
      return;
    }
    const directDownstreamLeadId = directDownstreamLeadIdRequested();
    if (directDownstreamLeadId) {
      await renderDirectDownstreamLead(mount, directDownstreamLeadId, bindLeadFactoryScreenActions);
      return;
    }
    if (directContactAcquisitionRequested()) {
      await renderDirectContactAcquisitionWorkbench(mount, bindLeadFactoryScreenActions);
      return;
    }
    if (directContactWorkRequested()) {
      await renderDirectContactWork(mount, bindLeadFactoryScreenActions);
      return;
    }
    if (directFollowUpRequested()) {
      await renderDirectFollowUp(mount, bindLeadFactoryScreenActions);
      return;
    }
    const directSavedViewManagerViewId = directSavedViewManagerId();
    if (directSavedViewManagerViewId) {
      await renderDirectSavedViewManager(mount, directSavedViewManagerViewId, bindLeadFactoryScreenActions);
      return;
    }
    await renderPipelineBoard(mount);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      renderAuthPrompt(error.message);
      return;
    }
    mount.innerHTML = `
      <main class="error-state">
        <h1>Dashboard data unavailable</h1>
        <p>${escapeHtml(error instanceof Error ? error.message : "Unknown dashboard load failure.")}</p>
      </main>
    `;
  }
}

function renderAuthPrompt(message: string): void {
  mount.innerHTML = `
    <main class="auth-shell">
      <form id="dashboard-auth-form" class="auth-panel">
        <p class="eyebrow">County Preforeclosure Pipeline</p>
        <h1>Dashboard Access</h1>
        <p class="subtle">${escapeHtml(message)}</p>
        <label>
          <span>Password</span>
          <input name="password" type="password" autocomplete="current-password" required autofocus />
        </label>
        <button class="button primary" type="submit">Unlock Dashboard</button>
        <p id="auth-status" class="action-status subtle">Full dashboard access is stored only in this browser.</p>
      </form>
    </main>
  `;
  const form = mustFind<HTMLFormElement>("#dashboard-auth-form");
  const status = mustFind<HTMLElement>("#auth-status");
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const formData = new FormData(form);
    const password = String(formData.get("password") ?? "").trim();
    if (!password) {
      status.textContent = "Enter the dashboard password.";
      return;
    }
    window.localStorage.setItem(dashboardTokenStorageKey, password);
    window.localStorage.setItem(dashboardOperatorStorageKey, "admin");
    window.localStorage.setItem(dashboardRoleStorageKey, "admin");
    void boot();
  });
}

function leadFactoryScreenActions() {
  return { refresh: () => void boot(), onAuthRequired: renderAuthPrompt };
}

function bindLeadFactoryScreenActions(statusElement: HTMLElement): void {
  bindLeadFactoryActions(statusElement, leadFactoryScreenActions(), showReview);
}

function leadDetailHost() {
  return {
    ...leadFactoryScreenActions(),
    bindNavigation: ensureLeadDetailNavigationBound
  };
}

function showReview(sourceDocumentId: string, statusElement: HTMLElement, focus?: SourceDocumentReviewFocus) {
  return showSourceDocumentReview(sourceDocumentId, statusElement, leadDetailHost(), focus);
}

void boot();
